import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { DialogActions, Grid, Typography, Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import EMPButton from "../../EMPButton/EMPButton";
import EMPCheckbox from "../../EMPCheckbox/EMPCheckbox";
import { ModalContext } from "../ModalContext";
import {
  doesAttestationExist,
  setAttestation,
} from "../../../services/localstorage/LocalStorageService";
import {
  trackEVPowerPromisePopupAction,
  trackEVPowerPromisePopupLoad,
} from "../../../services/analytics/OrderDetailsPageAnalytics";
import { UserContext } from "../../../context/userContext";
import { trackReferralExit } from "../../../services/analytics/AnalyticsService";

const EV_POWER_PROMISE_ATTESTATION_KEY = "ev-power-promise";

const EVPowerPromise = ({ orderId }) => {
  const { handleModal } = useContext(ModalContext);
  const [checked, setChecked] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const queryClient = useQueryClient();
  const { user } = useContext(UserContext);

  const { customerReservation } = queryClient.getQueryData([
    "order",
    { orderId },
  ]);

  const { vinNumber } = customerReservation;
  const customLink = `https://www.smartvincent.dealerconnection.com/smartvincent/svaccess.do?vin=${vinNumber}&source=vehiclelocator&Language=E`;

  useEffect(() => {
    const existingAttestation = doesAttestationExist(
      orderId,
      EV_POWER_PROMISE_ATTESTATION_KEY
    );

    if (existingAttestation) {
      setChecked(true);
      setDisabled(true);
    }
  }, [orderId]);

  useEffect(() => {
    trackEVPowerPromisePopupLoad(orderId, user);
  }, [orderId, user]);

  const handleChange = (checkedBool) => {
    if (doesAttestationExist(orderId, EV_POWER_PROMISE_ATTESTATION_KEY)) {
      return;
    }

    setChecked(checkedBool);
    setDisabled(!checkedBool);
  };

  const handleReferralClick = () => {
    trackReferralExit(customLink, orderId, user);
  };

  const handleCancel = () => {
    handleModal(false, false);
    trackEVPowerPromisePopupAction("cancel", orderId, user);
  };

  const saveAttestation = () => {
    setAttestation(EV_POWER_PROMISE_ATTESTATION_KEY, orderId, new Date());
    handleModal(false, false);
    queryClient.invalidateQueries("order", { orderId });

    trackEVPowerPromisePopupAction("confirm completed", orderId, user);
  };

  return (
    <div>
      <Typography variant="body1" sx={{ mb: 6 }}>
        Enroll your customer in the Ford Power Promise complimentary Home
        Charger and Standard Installation promotion through Smart Vincent.{" "}
        <Link
          to={{
            pathname: customLink,
          }}
          target="_blank"
          referrer="noreferrer"
          onClick={handleReferralClick}
        >
          Visit Smart Vincent
        </Link>
      </Typography>

      <Box sx={{ mb: 10 }}>
        <EMPCheckbox
          label="I attest that I have submitted info for the Ford Power Promise."
          checked={checked}
          handleChange={handleChange}
        />
      </Box>

      <DialogActions sx={{ padding: "16px 0" }}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <EMPButton variant="text" onClick={handleCancel}>
              Cancel
            </EMPButton>
          </Grid>

          <Grid item>
            <EMPButton
              variant="contained"
              onClick={saveAttestation}
              disabled={disabled}
            >
              Confirm Completed
            </EMPButton>
          </Grid>
        </Grid>
      </DialogActions>
    </div>
  );
};

EVPowerPromise.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default EVPowerPromise;
